import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { PersistentStorageManager } from '../../../../models/Storage';

import { CreateCampaignApprovalViewModel } from '../../../../viewmodels/AdminViewModels';
import { IAccount, IAccountTag, IOperationResultNoValue } from '../../../../viewmodels/AppViewModels';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { AutoCompleteSearchField } from '../../../../web/components/autocomplete/AutoCompleteSearchField';
import { GroupEmailTagSelect } from '../../../../web/components/email/guide/GroupEmailTagSelect';
import { LoadingTagsPlaceholder } from '../../../../web/components/email/guide/LoadingTagsPlaceholder';
import {
	TagSearchesStorageKey,
	updateTagsRecentSearchesLS,
} from '../../../../web/components/entities/tags/TagsEditor/hooks';
import { FeaturedAndRecentTags } from '../../../../web/components/entities/tags/TagsEditor/presentation';
import { SearchIcon } from '../../../../web/components/svgs/icons/SearchIcon';
import { styleSheet } from './styles';

interface IProps
	extends IEventLoggingComponentProps,
		IUserSessionComponentProps,
		RouteComponentProps<any>,
		IErrorMessageComponentProps {
	account: IAccount;
	approval: CreateCampaignApprovalViewModel;
	className?: string;
	onTagSelected?(tag: string): void;
	styles?: StyleDeclarationValue[];
}

interface IState {
	isLoading?: boolean;
	isOpen: boolean;
	value: string;
	recentSearches: (string | IAccountTag)[];
}
class _BulkEmailApprovalTagSelect extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);
		this.state = {
			isLoading: true,
			isOpen: false,
			value: '',
			recentSearches: [],
		};
	}

	public componentDidMount() {
		const { logPageView, approval, errorMessages } = this.props;
		const storedContactsQueryStringValue = PersistentStorageManager.local.get(TagSearchesStorageKey);
		if (storedContactsQueryStringValue) {
			this.setState({ recentSearches: JSON.parse(storedContactsQueryStringValue) });
		}

		logPageView('BulkEmailApprovalTagSelect');

		approval
			.loadSuggestedTags()
			?.then(() => {
				this.setState({ isLoading: false });
			})
			?.catch((err: IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
			});
	}

	public render() {
		const { className, styles, approval } = this.props;
		return (
			<div
				className={`${css(styleSheet.container, ...(styles || []))} bulk-email-approval-tag-select ${className || ''}`}
			>
				<MultiContainerHeader fullscreenHeader='Schedule Email for Clients: Pick Tag' />
				{approval.isLoadingSuggestedTags ? (
					<LoadingTagsPlaceholder className={css(styleSheet.loadingTagsPlaceholder)} title='Loading Tags...' />
				) : (
					<>
						{approval.suggestedTags?.length > 0 ? (
							<GroupEmailTagSelect
								onRenderAdvancedNode={this.onRenderAdvancedSearchBox}
								onTagSelected={this.onTagSelected}
								recommendedTags={approval.suggestedTags}
							/>
						) : (
							<div>No tags found.</div>
						)}
					</>
				)}
			</div>
		);
	}

	private onRenderAdvancedSearchBox = () => {
		const { value } = this.state;
		return (
			<AutoCompleteSearchField
				anchorClassName={css(styleSheet.searchBox)}
				anchorStyles={[styleSheet.anchorStyles]}
				dropdownContentClassName={css(styleSheet.autocompleteBox)}
				dropdownClassName={css(styleSheet.dropdownClassName)}
				inputId='tagSearchBox'
				sectionTitleElement={
					<>
						<div className={css(styleSheet.sectionWrap)}>
							<p className={css(styleSheet.sectionTitle)}>Top Results</p>
						</div>
					</>
				}
				emptyResults={
					<>
						<FeaturedAndRecentTags
							onSelected={(tag: IAccountTag) => {
								this.onAutocompleteTagSelected(tag as string);
							}}
							showNoResults={Boolean(value)}
						/>
					</>
				}
				inputProps={{
					placeholder: 'Search',
					onChange: this.handleValueChange,
					onFocus: () => this.setState({ isOpen: true }),
					onBlur: () => this.setState({ isOpen: false }),
				}}
				leftAccessory={
					<span className={css(styleSheet.iconSpan)}>
						<SearchIcon />
					</span>
				}
				onCreateAutoCompleteViewModel={this.onCreateAutoCompleteViewModel}
				onItemSelected={this.onAutocompleteTagSelected}
				pageSize={25}
				resultsLimit={25}
				type={Api.ResourceAutoCompleteViewModelType.Tag}
				openOnFocus={true}
				isOpen={this.state.isOpen}
			/>
		);
	};

	private handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ value: event.target.value });
	};

	private onCreateAutoCompleteViewModel = (
		_: Api.ResourceAutoCompleteViewModelType,
		suggestedVm: Api.ResourceAutoCompleteViewModel
	) => {
		const { approval } = this.props;
		return suggestedVm.impersonate({ account: approval.account });
	};

	private onAutocompleteTagSelected = (tag: string) => {
		this.onTagSelected(tag);
		if (tag !== undefined && tag !== null && tag !== '') {
			const { recentSearches } = this.state;
			const newRecentSearches = recentSearches.filter((t: string | IAccountTag) => t !== tag);
			this.setState({
				recentSearches: [tag, ...newRecentSearches].slice(0, 5),
				value: '',
			});
			updateTagsRecentSearchesLS(tag, newRecentSearches as string[]);
		}
	};

	private onTagSelected = (tag: string) => {
		const { onTagSelected } = this.props;
		if (onTagSelected) {
			onTagSelected(tag);
		}
	};
}

const BulkEmailApprovalTagSelectAsObserver = observer(_BulkEmailApprovalTagSelect);
const BulkEmailApprovalTagSelectWithContext = inject(
	UserSessionViewModelKey,
	ErrorMessagesViewModelKey
)(BulkEmailApprovalTagSelectAsObserver);
export const BulkEmailApprovalTagSelect = withEventLogging(
	BulkEmailApprovalTagSelectWithContext,
	'BulkEmailApprovalTagSelect'
);
